import React, { useEffect, useMemo, useState } from 'react';
import { StudyPlanEntity } from 'Models/Entities';
import Modal from 'Views/Components/Modal/Modal';
import { TextField } from 'Views/Components/TextBox/TextBox';
import { DisplayType } from 'Views/Components/Models/Enums';
import { Button, Display } from 'Views/Components/Button/Button';

interface IStudyPlanNameModalProps {
	isOpen: boolean,
	closeModal: () => any;
	onConfirm: (studyPlan: StudyPlanEntity) => any,
	initialPlan: StudyPlanEntity,
	existingStudyPlanNames?: string[]
}

const StudyPlanNameModal = (
	{
		isOpen, closeModal, onConfirm, initialPlan, existingStudyPlanNames,
	}: IStudyPlanNameModalProps,
) => {
	const [
		studyPlan, setStudyPlan,
	] = useState<StudyPlanEntity>(initialPlan);

	const isEditingPlan = useMemo(() => {
		return !!studyPlan.id;
	}, [studyPlan.id]);

	const [error, setError] = useState<string>();

	useEffect(() => {
		setStudyPlan(initialPlan);
	}, [initialPlan]);

	const clearModal = () => {
		setStudyPlan(new StudyPlanEntity());
		setError(undefined);
	};

	const onCancel = () => {
		clearModal();
		closeModal();
	};

	const onConfirmBtnClick = () => {
		if (!studyPlan.name || studyPlan.name?.length <= 0) {
			setError('Please provide a name for your study plan');
			return;
		}

		if (existingStudyPlanNames?.includes(studyPlan.name)) {
			setError('Study plan name already exists');
			return;
		}

		onConfirm(new StudyPlanEntity(studyPlan));
		clearModal();
	};

	return (
		<Modal
			isOpen={isOpen}
			label="New study plan"
			onRequestClose={onCancel}
		>
			<h3>{isEditingPlan ? 'Study plan name' : 'New study plan'}</h3>
			<TextField
				model={studyPlan}
				modelProperty="name"
				id="study-plan-name"
				name="study-plan-name"
				displayType={DisplayType.BLOCK}
				label="Name"
				labelVisible
				placeholder="Enter study plan name"
				onAfterChange={evt => {
					setError(undefined);
					setStudyPlan(prevState => {
						return new StudyPlanEntity({
							...prevState,
							name: evt.target.value,
						});
					});
				}}
				isRequired
				errors={error}
			/>
			<div key="actions" className="modal__actions">
				<Button key="cancel" className="modal--cancel" onClick={onCancel} display={Display.Text}>Cancel</Button>
				<Button
					key="confirm"
					className="modal--confirm"
					testId="next-btn"
					onClick={onConfirmBtnClick}
					display={Display.Solid}
					icon={!isEditingPlan ? {
						icon: 'arrow-forward',
						iconPos: 'icon-right',
					} : undefined}
				>
					{isEditingPlan ? 'Save' : 'Next'}
				</Button>
			</div>
		</Modal>
	);
};

export default StudyPlanNameModal;
